import { useEffect, useMemo } from "react";
import type { AccentName, LayoutPreset, Me } from "@/types/api";

/**
 * Per-tenant layout/accent picker.
 *
 * Phase 1: derived deterministically from tenantId so different tenants
 * automatically look distinct out of the box. Once Tenant Settings are wired
 * up, this hook will read `tenants.settings.layoutPreset` and
 * `tenants.settings.accent` and fall back to this round-robin only if unset.
 */
const PRESETS: LayoutPreset[] = ["left-pinned", "icon-rail", "glass-floating"];
const ACCENTS: AccentName[] = ["rose", "violet", "emerald", "cyan", "amber"];

export function useTenantLayout(me: Me | undefined | null) {
  const choice = useMemo(() => {
    const tid = me?.tenantId ?? 1;
    return {
      preset: PRESETS[(tid - 1) % PRESETS.length],
      accent: ACCENTS[(tid - 1) % ACCENTS.length],
    };
  }, [me?.tenantId]);

  // The accent used to be written to <html data-tenant-accent>, which
  // repainted brokers in a round-robin colour. Branding now comes solely
  // from tenants.primary_color (lib/theme.ts), so only clear stale values.
  useEffect(() => {
    if (document.documentElement.dataset.tenantAccent !== "violet") delete document.documentElement.dataset.tenantAccent;
  }, []);

  return choice;
}
